import { toPlainText } from 'next-sanity'
import { useRouter } from 'next/router'
import numeral from 'numeral'
import { ReactNode, useMemo } from 'react'

import { Image } from 'src/components/atoms/Image'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

import { HeroCtaContainer } from './HeroCtaContainer'
import { HeroTitle, HeroTitleTypography } from './HeroTitleTypography'
import {
  HeroRebrandLayout,
  HeroRebrandLayoutProps
} from './layouts/HeroRebrandLayout'
import { BackgroundPattern, HeroBackgroundPattern } from './types'

type BusinessPackageHeroRebrandProps = {
  title: HeroTitle
  eyebrow?: string
  intro?: string
  ctas?: ReactNode
  imageUrl: string
  layout: HeroBackgroundPattern
  unified?: boolean
  mode?: HeroRebrandLayoutProps['mode']
  pattern: BackgroundPattern
  darkHeader?: boolean
  monthlyAmount: number
  annualAmount: number
}

const BusinessPackageHeroRebrand = ({
  title,
  ctas,
  eyebrow,
  imageUrl,
  intro,
  layout,
  unified,
  mode,
  pattern,
  darkHeader,
  monthlyAmount,
  annualAmount
}: BusinessPackageHeroRebrandProps) => {
  const router = useRouter()
  // Frequency comes from the /packages section's Monthly/Annual tab via the
  // ?frequency= query param appended to each tier's Learn more link. Default
  // to monthly when missing or invalid (covers direct hits to /packages/<tier>
  // without going through the section).
  const isAnnual = router.query.frequency === 'annual'
  const cadenceLabel = isAnnual ? 'Yearly' : 'Monthly'

  const formattedAmount = useMemo(() => {
    return numeral(isAnnual ? annualAmount : monthlyAmount).format('$0,00')
  }, [isAnnual, monthlyAmount, annualAmount])

  const amountContainerClasses = useMemo(() => {
    let className = ''
    switch (pattern) {
      case 'patternLavenderSoft':
        className = `bg-limeBright text-forestDeep`
        break
      case 'patternLimeBright':
        className = 'bg-forestDeep text-limeBright'
        break
      case 'patternPeach':
        className = 'bg-wineRich text-peach'
        break
      case 'patternGreenMarble':
        className = 'bg-limeBright text-forestDeep'
        break
      default:
        className = ''
        break
    }
    return className
  }, [pattern])

  return (
    <HeroRebrandLayout
      darkHeader={darkHeader}
      mode={mode}
      layout={layout}
      unified={unified}
      pattern={pattern}
    >
      <div className='flex flex-col-reverse md:flex-row pb-15 md:py-15 items-center gap-6 md:gap-3'>
        <div className='hero-text-content flex-1 flex flex-col w-full items-center md:items-baseline'>
          {eyebrow ? (
            <div className='mb-4'>
              <TypographyRebrand variant='eyebrow'>{eyebrow}</TypographyRebrand>
            </div>
          ) : null}
          <HeroTitleTypography title={title} variant='h2' />
          {intro ? (
            <TypographyRebrand variant='introduction'>
              {intro}
            </TypographyRebrand>
          ) : null}
          {ctas ? <HeroCtaContainer>{ctas}</HeroCtaContainer> : null}
        </div>
        <div className='md:flex-1 w-full flex relative h-60 md:h-80 lg:h-125'>
          <Image
            className='rounded-xl object-cover'
            alt={typeof title === 'string' ? title : toPlainText(title)}
            src={imageUrl}
            fill
          />
          <div
            className={`absolute -left-5 top-10 [&_div]:m-0 p-4 rounded-lg md:rounded-xl flex flex-col ${amountContainerClasses}`}
          >
            <TypographyRebrand variant='paragraph'>From</TypographyRebrand>
            <div className='flex flex-row items-end gap-1'>
              <TypographyRebrand variant='h4'>
                {formattedAmount}
              </TypographyRebrand>
              <TypographyRebrand variant='paragraph'>
                {cadenceLabel}
              </TypographyRebrand>
            </div>
          </div>
        </div>
      </div>
    </HeroRebrandLayout>
  )
}

export default BusinessPackageHeroRebrand
